import { Feature, FeatureId } from '../core/client/features.gen'

export interface Department {
  id: string;
  name: string;
  description?: string;
  icon_url?: string;
}

export interface JobCategory {
  id: string;
  name: string;
  description: string;
  department_id: string;
  icon_url?: string;
}

// How Phoenix tracing is decided for conversations using an agent:
// always trace, never trace, or only when a tracing-enabled tool is invoked.
export type AgentTracingMode = 'always' | 'never' | 'tool_based'

// The main chat agent's slug, as the backend seeds it.
export const MAIN_CHAT_AGENT_SLUG = 'vanilla'

// Privileged agents live in the sidebar, not the library; Reshaped admins also
// get the main chat there, to edit it.
export const isInLibrary = (agent: Pick<Agent, 'type' | 'slug'>, isReshapedAdmin: boolean): boolean =>
  agent.type !== 'privileged' || (agent.slug === MAIN_CHAT_AGENT_SLUG && isReshapedAdmin)

// Tools the editor switches on or off as a whole, each only where its feature is on.
export const SWITCHED_TOOLS: { name: string; feature: FeatureId; labelKey: string }[] = [
  { name: 'KNOWLEDGE_SEARCH', feature: Feature.INTERNAL_SEARCH, labelKey: 'createAgent.toolsKnowledgeSearch' },
  { name: 'GRAPH_SEARCH', feature: Feature.GRAPH_SEARCH, labelKey: 'createAgent.toolsGraphSearch' },
  { name: 'CLARIFICATION', feature: Feature.CLARIFICATION, labelKey: 'createAgent.toolsClarification' },
  { name: 'INTERNET_SEARCH', feature: Feature.INTERNET_SEARCH, labelKey: 'createAgent.toolsInternetSearch' },
]
export const SWITCHED_TOOL_NAMES = SWITCHED_TOOLS.map(tool => tool.name)
export const visibleSwitchedTools = (isEnabled: (feature: FeatureId) => boolean) =>
  SWITCHED_TOOLS.filter(tool => isEnabled(tool.feature))

// Limits the backend enforces on agent routing; keep in step with the API.
export const MAX_ROUTING_DESCRIPTION_LENGTH = 1000
export const MIN_ROUTING_EXAMPLE_QUESTIONS = 3

export interface Agent {
  id: string
  name: string
  description: string | null
  profile_picture_url: string | null
  type: 'privileged' | 'normal'
  created_by_id: number | null
  created_at: string
  updated_at: string | null
  department: Department | null
  job_category: JobCategory | null
  tags: Tag[]
  is_preferred: boolean
  example_questions: string[]
  slug?: string;
  // Enabled tool identifiers. Built-in tools use fixed literals
  // ('FILE_SEARCH' enables file attachments, 'INTERNET_SEARCH' forces web
  // search); per-source knowledge tools use `retrieve_knowledge_<source>`
  // names, so this is an open string list.
  tools?: string[]
  // Only present on the detail (edit) response
  tracing_mode?: AgentTracingMode
  // Only present on the detail (edit) response.
  is_public?: boolean
  client_entity_ids?: number[]
  // Flag types that do NOT apply to this agent. Empty/absent means every
  // active flag applies — the default. Only present on the detail response.
  excluded_flag_types?: string[]
  // Retrieval width ("k"): how many documents a knowledge search returns for
  // this agent. Null means the backend default. Only on the detail response.
  knowledge_top_k?: number | null
  // routing_description is null for non-superusers.
  available_in_main_chat?: boolean
  routing_description?: string | null
  use_client_context?: boolean
}

// One active flag classifier, as offered in the assistant editor
// (`GET /api/flags/`). `type` is the key stored in `excluded_flag_types`.
export interface FlagDefinition {
  type: string
  display_name: string
  severity: 'info' | 'warning' | 'critical'
}

export interface ClientEntityRef {
  id: number
  name: string
}

export interface Tag {
  name: string;
}

export interface CreateTagData {
  name: string;
}
